
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { mapGetters, mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import { required, maxLength, requiredIf, minLength, sameAs, email, integer } from '@vuelidate/validators';
import { adService } from './AdService';
import { accountService } from '../account/AccountService';
import store from '@/store';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import SortableItem from '@/shared/components/SortableItem.vue';
import SortableList from '@/shared/components/SortableList.vue';
import MapPosition from '@/shared/components/MapPosition.vue';
import LoginButton from '@/account/components/LoginButton.vue';
import FlashMessage from '@/shared/components/FlashMessage.vue';
import FixedElement from '@/shared/components/FixedElement.vue';
import { Address } from '@/shared/models/Address';
import { Message, MessageType } from '@/shared/models/Message';
import { UploadFile } from '@/shared/models/UploadFile';
import { HandleDirective } from 'vue-slicksort';
import config from '@/shared/constants/config';
import { Point } from '@/shared/models/Point';
import AdPreview from './AdPreview.vue';
import EstateAgentReviewRequestForm from '@/estate-agent-review/components/EstateAgentReviewRequestForm.vue';
import { environment } from '@/environment/environment';
import useVuelidate from '@vuelidate/core';
import { scrollToAnchor } from '@/shared/helpers/scrollToAnchor';
import PhonePrefix from '@/shared/components/PhonePrefix.vue';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';
import decimal from '@/shared/helpers/decimalValidator';
import minValue from '@/shared/helpers/minValueValidator';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';
import { estateAgencyService } from '@/estate-agency/EstateAgencyService';
import { countries, defaultCountry } from '@/shared/constants/mapCountries';
import Multiselect from 'vue-multiselect';

export default defineComponent({
  mixins: [Form],
  setup() {
    return { v$: useVuelidate({ $scope: false }) };
  },
  components: {
    ResizableImage,
    SortableItem,
    SortableList,
    MapPosition,
    LoginButton,
    FlashMessage,
    AdPreview,
    FixedElement,
    EstateAgentReviewRequestForm,
    PhonePrefix,
    Multiselect,
  },
  props: {
    adFrontId: String,
    afterInsert: Boolean,
    isCompetition: Boolean,
  },
  directives: { handle: HandleDirective },
  data() {
    return {
      bottomThreshold: 289,
      privacyUrl: config.PRIVACY_URL,
      generalTermsUrl: config.GENERAL_TERMS_URL,
      competitionTermsUrl: config.COMPETITION_TERMS_URL,
      domain: config.DOMAIN,
      showPreview: false,
      isHeaderFixed: false,
      messages: [] as Message[],
      notificationClass: '',
      initialPosition: null as null | Point,
      isInserted: false,
      isPublished: false,
      isOwner: false,
      isConfirmEmailSent: false,
      placeholderImagesCount: 3,
      logoUrl: config.BASE_URL + '/img/logo.svg',
      placeholderUrl: config.BASE_URL + '/img/upload_gallery_noimage.svg',
      defaultArea: 7,
      isUploading: false,
      filesToUpload: [] as string[] | Blob[],
      dragAndDropCapable: false,
      estateTypes: [],
      estateSubTypes: [],
      country: null,
      countries: null,
      adSexes: [],
      adAges: [],
      statuses: [],
      developerProjectStatuses: [],
      hasBalcony: false,
      hasLoggia: false,
      hasTerrace: false,
      hasParking: false,
      hasCellar: false,
      hasGarage: false,
      hasLift: false,
      estateAgents: [],
      publishStatuses: ['publish', 'reserve', 'archive'],
      publishStatus: 'publish',
      form: {
        adId: 0,
        adFrontId: null as null | string,
        adGuid: '',
        adType: 1,
        estateFrontSubType: 101,
        roomCount: null as null | number,
        street: '',
        streetNumber: '',
        city: '',
        countryCode: '',
        latitude: null as null | number,
        longitude: null as null | number,
        title: '',
        description: '',
        price: null as null | number,
        hasEmptyPrice: false,
        currency: 'EUR',
        apartmentArea: null as null | number,
        usefulArea: null as null | number,
        estateArea: null as null | number,
        landAreaUnit: 'm2',
        landArea: null as null | number,
        builtUpAreaUnit: 'ha',
        builtUpArea: null as null | number,
        files: [] as UploadFile[],
        address: null as null | string,
        userName: null as null | string,
        email: null as null | string,
        phoneNumberPrefix: config.DEFAULT_PHONE_PREFIX,
        phoneNumber: null as null | string,
        confirmEmail: null as null | string,
        password: null as null | string,
        confirmPassword: null as null | string,
        generalTermsAgreement: false,
        personalAgreement: false,
        competitionTermsAgreement: false,
        isPublished: false,
        isEmailConfirmed: false,
        personCount: 1 as null | number,
        bedCount: 1 as null | number,
        adSex: null as null | number,
        adAge: null as null | number,
        availableFrom: null as null | Date,
        energyPrice: null as null | number,
        web: null as null | string,
        virtualTourLink: null as null | string,
        videoLink: null as null | string,
        status: null as null | number,
        developerProjectStatus: null as null | number,
        floor: null as null | number,
        maxFloor: null as null | number,
        hasCellar: null as null | boolean,
        balconyArea: null as null | number,
        loggiaArea: null as null | number,
        terraceArea: null as null | number,
        parkingPlaceCount: null as null | number,
        garageCount: null as null | number,
        liftCount: null as null | number,
        cellarCount: null as null | number,
        hasWaterNetwork: null as null | boolean,
        hasGasNetwork: null as null | boolean,
        hasCanalizationNetwork: null as null | boolean,
        hasElectricityNetwork: null as null | boolean,
        hasPetsAllowed: null as null | boolean,
        hasSmokersAllowed: null as null | boolean,
        frontUserId: null as null | number,
        isArchived: null as null | boolean,
        isReserved: null as null | boolean,
        hasReview: null as null | boolean,
      },
    };
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventNav);
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    const shouldAlert = this.isEditing;
    if (shouldAlert) {
      const answer = window.confirm(this.$t('page-leave-confirm'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
  validations() {
    const validations = {
      form: {
        adType: { required },
        estateFrontSubType: { required },
        roomCount: { integer, minValue: minValue(0) },
        address: { required },
        street: {},
        streetNumber: {},
        city: {},
        countryCode: { required },
        latitude: { required },
        longitude: { required },
        title: { required, maxLength: maxLength(70) },
        description: { required },
        price: {
          required: requiredIf(() => {
            return !this.form.hasEmptyPrice;
          }),
          integer,
          minValue: minValue(0),
        },
        hasEmptyPrice: {},
        currency: { required },
        apartmentArea: { decimal, minValue: minValue(0) },
        usefulArea: {
          required: requiredIf(function (this: any) {
            return this.isHouse;
          }),
          decimal,
          minValue: minValue(0),
        },
        estateArea: {
          required: requiredIf(() => {
            return !this.isLand;
          }),
          decimal,
          minValue: (estateArea) => {
            let result = this.isLand ? true : +estateArea.toString().replaceAll(',', '.') > 0;
            return result;
          },
        },
        web: {},
        virtualTourLink: {},
        videoLink: {},
        status: {},
        developerProjectStatus: {},
        floor: {
          required: requiredIf(() => {
            return this.form && this.form.maxFloor ? this.form.maxFloor > 0 : false;
          }),
          integer,
        },
        maxFloor: {
          required: requiredIf(() => {
            return this.form && this.form.floor ? this.form.floor > 0 : false;
          }),
          integer,
        },
        liftCount: {},
        garageCount: {},
        cellarCount: {},
        hasCellar: {},
        balconyArea: {},
        loggiaArea: {},
        terraceArea: {},
        parkingPlaceCount: {},
        hasWaterNetwork: {},
        hasGasNetwork: {},
        hasCanalizationNetwork: {},
        hasElectricityNetwork: {},
        hasPetsAllowed: {},
        hasSmokersAllowed: {},
        personCount: {
          required: requiredIf(function (this: any) {
            return this.isRoommate;
          }),
          decimal,
        },
        bedCount: {
          required: requiredIf(function (this: any) {
            return this.isRoommate;
          }),
          decimal,
        },
        adAge: {
          required: requiredIf(function (this: any) {
            return this.isRoommate;
          }),
          decimal,
        },
        adSex: {
          required: requiredIf(function (this: any) {
            return this.isRoommate;
          }),
          decimal,
        },
        availableFrom: {},
        energyPrice: { decimal, minValue: minValue(0) },
        landArea: {
          required: requiredIf(function (this: any) {
            return this.isHouse || this.isLand;
          }),
          decimal,
          minValue: minValue(0),
        },
        landAreaUnit: {
          required: requiredIf(function (this: any) {
            return this.isHouse || this.isLand;
          }),
        },
        builtUpArea: {
          decimal,
          minValue: minValue(0),
        },
        builtUpAreaUnit: {},
        userName: {
          required: requiredIf(function (this: any) {
            return !this.isLoggedIn;
          }),
        },
        phoneNumberPrefix: {},
        phoneNumber: {
          phoneNumber: phoneNumber(this.form.phoneNumberPrefix),
        },
        email: {
          required: requiredIf(function (this: any) {
            return !this.isLoggedIn;
          }),
          email,
        },
        confirmEmail: {},
        password: {
          required: requiredIf(function (this: any) {
            return !this.isLoggedIn;
          }),
          minLength: minLength(8),
        },
        confirmPassword: {
          required: requiredIf(function (this: any) {
            return !this.isLoggedIn;
          }),
          sameAsPassword: sameAs(this.form.password),
        },
        generalTermsAgreement: {},
        personalAgreement: {},
        competitionTermsAgreement: {},
        frontUserId: {},
      },
    };
    if (!this.isLoggedIn) {
      validations.form.generalTermsAgreement = { sameAs: sameAs(true) };
      validations.form.personalAgreement = { sameAs: sameAs(true) };
      validations.form.confirmEmail = {
        required,
        sameAsEmail: sameAs(this.form.email),
      };
    }
    if (this.isCompetition) {
      validations.form.competitionTermsAgreement = { sameAs: sameAs(true) };
    }
    return validations;
  },
  watch: {
    windowWidth: function () {
      this.updateBottomThreshold();
    },
    isLoggedIn: function (newVal) {
      if (newVal) {
        this.form.userName = null;
        this.form.email = null;
        this.form.confirmEmail = null;
        this.form.password = null;
        this.form.confirmPassword = null;
      }
    },
  },
  async created() {
    this.country = defaultCountry;
    this.countries = countries;
    this.updateBottomThreshold();
    this.isLoading = true;
    try {
      if (this.isEdit) {
        this.isInserted = this.afterInsert;
        await this.loadData();
        scrollToAnchor(this.$route.hash);
      } else {
        const result = await adService.getNew();
        this.form.adGuid = result.data.model.adGuid;
        this.estateTypes = result.data.estateTypes;
        this.estateSubTypes = result.data.estateSubTypes;
        this.adSexes = result.data.adSexes;
        this.adAges = result.data.adAges;
        this.statuses = result.data.statuses;
        this.developerProjectStatuses = result.data.developerProjectStatuses;

        if (localStorage) {
          let lsForm = localStorage.getItem('adForm');
          if (lsForm) {
            this.form = JSON.parse(lsForm);
            this.initSavedForm();
          }
        }
        this.isLoading = false;
      }
      this.handleFiles();
      this.handleFlashMessages();

      if (this.user && this.user.isAdmin) {
        await this.loadAgents();
      }
    } catch (error) {
      console.error(error);
    }
  },
  mounted() {
    this.dragAndDropCapable = this.determineDragAndDropCapable();

    if (this.dragAndDropCapable) {
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
        (this.$refs.fileuploader as Element).addEventListener(
          evt,
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          false,
        );
      });
      (this.$refs.fileuploader as Element).addEventListener('drop', (e: any) => {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          this.filesToUpload.push(e.dataTransfer.files[i]);
        }
        this.uploadFiles();
      });
    }
  },
  computed: {
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
    ...mapGetters(['isLoggedIn', 'windowWidth', 'isMobile']),
    isEditing(): boolean {
      return this.v$.$anyDirty && this.v$.$invalid;
    },
    showFormMessages(): boolean {
      return this.isSuccess || this.isError || this.errors.length > 0;
    },
    isInsert(): boolean {
      return !this.adFrontId;
    },
    isEdit(): boolean {
      return !this.isInsert;
    },
    isFlat(): boolean {
      return this.form.estateFrontSubType > 100 && this.form.estateFrontSubType < 200;
    },
    isHouse(): boolean {
      return this.form.estateFrontSubType > 200 && this.form.estateFrontSubType < 300;
    },
    isLand(): boolean {
      return this.form.estateFrontSubType > 300 && this.form.estateFrontSubType < 400;
    },
    isObject(): boolean {
      return this.form.estateFrontSubType > 400 && this.form.estateFrontSubType < 500;
    },
    isParkingSpace(): boolean {
      return this.form.estateFrontSubType === 401;
    },
    landArea(): number {
      const area = this.form.landArea ? this.form.landArea : this.defaultArea;
      return this.calculateArea(area, this.form.landAreaUnit);
    },
    builtUpArea(): number {
      const area = this.form.builtUpArea ? this.form.builtUpArea : this.defaultArea;
      return this.calculateArea(area, this.form.builtUpAreaUnit);
    },
    isShowEstateAgentReviewVisible(): boolean | null {
      return this.isEdit && this.user && this.user.isEstateAgent && this.form.adId > 0 && this.isOwner;
    },
    isSale(): boolean {
      return this.form.adType === 1;
    },
    isLease(): boolean {
      return this.form.adType === 2;
    },
    isRoommate(): boolean {
      return this.form.adType === 3;
    },
    pricePlaceholder(): string {
      if (this.isSale) {
        return this.$t('AdForm.price-placeholder.sale');
      }
      if (this.isLease) {
        return this.$t('AdForm.price-placeholder.lease');
      }
      if (this.isRoommate) {
        return this.$t('AdForm.price-placeholder.roommate');
      }
      return '';
    },
    isDeveloperProjectEstate(): boolean {
      return this.form.parentAdId && this.form.parentAdId > 0;
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const result = await adService.getById(this.adFrontId ? this.adFrontId : '');
      this.form = Object.assign({}, this.form, result.data.model);
      if (!result.data.model) {
        this.handleFlashMessages([new Message(this.$t('AdForm.not-found'), MessageType.Error)], true);
        return;
      }
      this.form.hasEmptyPrice = !this.form.price;
      this.isPublished = this.form.isPublished;
      this.isOwner = result.data.model.isOwner;
      this.estateTypes = result.data.estateTypes;
      this.estateSubTypes = result.data.estateSubTypes;
      this.adSexes = result.data.adSexes;
      this.adAges = result.data.adAges;
      this.statuses = result.data.statuses;
      this.developerProjectStatuses = result.data.developerProjectStatuses;
      this.initSavedForm();
      this.isLoading = false;
    },
    async loadAgents() {
      const resultAgents = await estateAgencyService.getProfileEstateAgents();
      this.estateAgents = resultAgents.data.data;
    },
    onEmptyPriceChange() {
      this.form.price = null;
    },
    updateBottomThreshold() {
      const footer = document.querySelector('footer');
      if (footer) {
        this.bottomThreshold = footer.clientHeight;
      }
    },
    preventNav(event: any) {
      if (!this.isEditing || environment !== 'production') {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
      if (localStorage && !this.isEdit) {
        const form = this.beforeSave();
        localStorage.setItem('adForm', JSON.stringify(Object.assign({}, form, { password: null, confirmPassword: null })));
      }
    },
    initSavedForm() {
      if (this.form.countryCode) {
        let country = this.countries.find((x) => x.key === this.form.countryCode.toLowerCase());
        if (country) {
          this.country = country;
        }
      }

      if (this.form.latitude && this.form.longitude) {
        this.initialPosition = { lat: this.form.latitude, lng: this.form.longitude };
      }
      if (this.form.balconyArea) {
        this.hasBalcony = this.form.balconyArea > 0;
      }
      if (this.form.loggiaArea) {
        this.hasLoggia = this.form.loggiaArea > 0;
      }
      if (this.form.terraceArea) {
        this.hasTerrace = this.form.terraceArea > 0;
      }
      if (this.form.parkingPlaceCount) {
        this.hasParking = this.form.parkingPlaceCount > 0;
      }
      if (this.form.liftCount) {
        this.hasLift = this.form.liftCount > 0;
      }
      if (this.form.cellarCount) {
        this.hasCellar = this.form.cellarCount > 0;
      }
      if (this.form.garageCount) {
        this.hasGarage = this.form.garageCount > 0;
      }

      if (this.form.files) {
        for (const item of this.form.files) {
          item.isSuccess = true;
        }
      }
      this.setLandArea(this.form.landArea);
      this.setBuiltUpArea(this.form.builtUpArea);
    },
    updateFixedStatus(isFixed: boolean) {
      this.isHeaderFixed = isFixed;
    },
    setLandArea(landArea: number | null) {
      if (!landArea) return;
      if (landArea > 10000) {
        this.form.landArea = landArea / 10000;
        this.form.landAreaUnit = 'ha';
      } else if (landArea > 100) {
        this.form.landArea = landArea / 100;
        this.form.landAreaUnit = 'a';
      } else {
        this.form.landArea = landArea;
        this.form.landAreaUnit = 'm2';
      }
    },
    setBuiltUpArea(builtUpArea: number | null) {
      if (!builtUpArea) return;
      if (builtUpArea > 10000) {
        this.form.builtUpArea = builtUpArea / 10000;
        this.form.builtUpAreaUnit = 'ha';
      } else if (builtUpArea > 100) {
        this.form.builtUpArea = builtUpArea / 100;
        this.form.builtUpAreaUnit = 'a';
      } else {
        this.form.builtUpArea = builtUpArea;
        this.form.builtUpAreaUnit = 'm2';
      }
    },
    handleFlashMessages(appendMessages: Message[] = [], notFound = false) {
      const messages = [] as any[];
      if (!notFound) {
        if (!this.form.isPublished) {
          if (!this.form.isEmailConfirmed) {
            messages.push(new Message(this.$t('AdForm.saved-not-published-not-confirmed'), MessageType.Warning, 'confirmemail'));
          } else {
            messages.push(new Message(this.$t('AdForm.saved-not-published'), MessageType.Error));
          }
        }
      }
      if (appendMessages && appendMessages.length > 0) {
        messages.push(...appendMessages);
      }
      this.messages = messages;
      this.notificationClass = this.getNotificationClass();
    },
    getNotificationClass() {
      const errorExists = this.messages.some((x) => x.type === MessageType.Error);
      if (errorExists) return 'is-danger';
      const warningExists = this.messages.some((x) => x.type === MessageType.Warning);
      if (warningExists) return 'is-warning';
      return 'is-success';
    },
    onAddressChanged(address: Address) {
      if (address) {
        this.form.street = address.street;
        this.form.streetNumber = address.streetNumber;
        this.form.city = address.city;
        this.form.countryCode = address.countryCode;
        this.form.latitude = address.latitude;
        this.form.longitude = address.longitude;
        this.form.address = address.address;
      } else {
        this.form.street = '';
        this.form.streetNumber = '';
        this.form.city = '';
        this.form.countryCode = '';
        this.form.latitude = null;
        this.form.longitude = null;
        this.form.address = null;
      }

      if (this.form.countryCode) {
        let country = this.countries.find((x) => x.key === this.form.countryCode.toLowerCase());
        if (country) {
          this.country = country;
        }
      }

      const validationControl = (this.v$.form as any).address;
      if (validationControl) {
        validationControl.$touch();
      }
    },
    onPublishChanged() {
      this.publish(this.isPublished);
    },
    async onReservedChanged() {
      try {
        this.isLoading = true;
        if (!this.form.isReserved) {
          await adService.reserve(this.form.adId);
        } else {
          await adService.unreserve(this.form.adId);
        }
        await this.loadData();
        this.handleFlashMessages();
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onArchivedChanged() {
      try {
        this.isLoading = true;
        if (!this.form.isArchived) {
          await adService.archive(this.form.adId);
        } else {
          await adService.unarchive(this.form.adId);
        }
        await this.loadData();
        this.handleFlashMessages();
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    adTypeClick(value: number) {
      this.form.adType = value;
    },
    addPlaceholder() {
      this.form.files.push({
        isPlaceholder: true,
      } as UploadFile);
    },
    addFile(file: UploadFile) {
      this.form.files.push(file);
      this.handleFiles();
    },
    deleteFile(index: number) {
      this.form.files.splice(index, 1);
      this.handleFiles();
    },
    handleFiles() {
      if (!this.form.files) {
        this.form.files = [];
      }
      this.form.files = this.form.files.filter((x) => !x.isPlaceholder);
      for (let i = this.form.files.length; i < this.placeholderImagesCount; i++) {
        this.addPlaceholder();
      }
    },
    determineDragAndDropCapable() {
      const div = document.createElement('div');
      return ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window;
    },
    async uploadFiles() {
      const files = this.filesToUpload;
      this.filesToUpload = [];
      if (files.length > 0) {
        this.isUploading = true;
        for (const file of files) {
          const uploadingFile: UploadFile = {} as UploadFile;
          uploadingFile.isUploading = true;
          uploadingFile.uploadPercentage = 0;
          uploadingFile.file = (file as any).name;
          this.addFile(uploadingFile);
          const formData = new FormData();
          formData.append('adGuid', this.form.adGuid);
          formData.append('file', file);
          try {
            const result = await adService.uploadImage(formData, (progressEvent: any) => {
              let file = this.form.files.find((f) => f.file === uploadingFile.file);
              if (file) {
                file.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
              }
            });
            uploadingFile.file = result.data.file;
            uploadingFile.url = result.data.url;
            uploadingFile.isSuccess = true;
          } catch (error) {
            uploadingFile.isError = true;
            uploadingFile.text = 'Error';
            const errors = this.getApiErrors(error);
            uploadingFile.text = errors[0].message;
          } finally {
            uploadingFile.isUploading = false;
          }
        }
        this.isUploading = false;
      }
    },
    async handleFilesUpload() {
      this.filesToUpload = (this.$refs.files as any).files as string[] | Blob[];
      await this.uploadFiles();
      (this.$refs.files as any).value = null;
    },
    async publish(value: boolean) {
      this.isInserted = false;
      try {
        this.isLoading = true;
        if (value) {
          await adService.publish(this.form.adId);
        } else {
          await adService.unpublish(this.form.adId);
        }
        this.form.isPublished = value;
        if (value) {
          this.handleFlashMessages([new Message(this.$t('AdForm.saved-published'), MessageType.Success)]);
        } else {
          this.handleFlashMessages();
        }
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    beforeSave() {
      const form = Object.assign({}, this.form);

      if (form.phoneNumberPrefix && form.phoneNumberPrefix.value) {
        form.phoneNumberPrefix = form.phoneNumberPrefix.value;
      }

      let landArea = this.calculateArea(this.form.landArea, this.form.landAreaUnit);
      landArea = landArea > 0 ? landArea : null;

      let builtUpArea = this.calculateArea(this.form.builtUpArea, this.form.builtUpAreaUnit);
      builtUpArea = builtUpArea > 0 ? builtUpArea : null;

      if (this.isLand) {
        form.estateArea = landArea;
      }
      form.landArea = landArea;
      form.builtUpArea = builtUpArea;

      form.files = form.files.filter((x) => x.isSuccess);

      if (this.hasBalcony && this.form.balconyArea && this.form.balconyArea > 0) {
        form.balconyArea = this.form.balconyArea;
      } else {
        form.balconyArea = null;
      }
      if (this.hasLoggia && this.form.loggiaArea && this.form.loggiaArea > 0) {
        form.loggiaArea = this.form.loggiaArea;
      } else {
        form.loggiaArea = null;
      }
      if (this.hasTerrace && this.form.terraceArea && this.form.terraceArea > 0) {
        form.terraceArea = this.form.terraceArea;
      } else {
        form.terraceArea = null;
      }
      if ((this.hasParking || this.isParkingSpace) && this.form.parkingPlaceCount && this.form.parkingPlaceCount > 0) {
        form.parkingPlaceCount = this.form.parkingPlaceCount;
      } else {
        form.parkingPlaceCount = null;
      }
      if (this.hasCellar && this.form.cellarCount && this.form.cellarCount > 0) {
        form.cellarCount = this.form.cellarCount;
      } else {
        form.cellarCount = null;
      }
      if (this.hasLift && this.form.liftCount && this.form.liftCount > 0) {
        form.liftCount = this.form.liftCount;
      } else {
        form.liftCount = null;
      }
      if (this.hasGarage && this.form.garageCount && this.form.garageCount > 0) {
        form.garageCount = this.form.garageCount;
      } else {
        form.garageCount = null;
      }
      return form;
    },
    async save(redirect: boolean) {
      this.isInserted = false;

      if (!this.validate()) {
        return;
      }
      const form = this.beforeSave();

      try {
        this.isLoading = true;
        if (this.isInsert) {
          const result = await adService.insert(form);
          if (localStorage) {
            localStorage.removeItem('adForm');
          }
          store.dispatch('loginForce', result.data.user);

          this.logSuccess();

          if (this.isCompetition) {
            this.$router.push({ name: 'AdThankYou', params: { adFrontId: result.data.adFrontId } });
          } else {
            this.$router.push({ name: 'AdUserList' });
          }
          //this.$router.push({ name: 'AdFormEdit', params: { adFrontId: result.data.adFrontId, afterInsert: 'true' } });
        } else {
          await adService.update(form);
          if (redirect) {
            this.$router.push({ name: 'AdUserList' });
          }
        }
        this.isSuccess = true;
        this.handleFlashMessages();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    logSuccess() {
      let event = '';
      if (this.isSale) {
        event = 'pridane_predaj';
      } else if (this.isLease) {
        event = 'pridane_prenajom';
      } else if (this.isRoommate) {
        event = 'pridane_spolubyvanie';
      }

      if (this.isCompetition) {
        event += '_sutaz';
      }
      googleTagManagerService.send({
        event: event,
      });
    },
    async sendConfirmEmail() {
      try {
        this.isLoading = true;
        await accountService.resetConfirmEmail();
        this.isConfirmEmailSent = true;
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onShowPreview() {
      await this.save(false);
      this.showPreview = true;
    },
    subtractBedCount() {
      if (typeof this.form.bedCount !== 'number') {
        this.form.bedCount = 1;
      } else if (this.form.bedCount > 1) {
        this.form.bedCount = this.form.bedCount - 1;
      }
    },
    addBedCount() {
      if (typeof this.form.bedCount !== 'number') {
        this.form.bedCount = 1;
      } else {
        this.form.bedCount = this.form.bedCount + 1;
      }
    },
    subtractPersonCount() {
      if (typeof this.form.personCount !== 'number') {
        this.form.personCount = 1;
      } else if (this.form.personCount > 1) {
        this.form.personCount = this.form.personCount - 1;
      }
    },
    addPersonCount() {
      if (typeof this.form.personCount !== 'number') {
        this.form.personCount = 1;
      } else {
        this.form.personCount = this.form.personCount + 1;
      }
    },
    notBeforeToday(date: Date) {
      return date < new Date(new Date().setHours(0, 0, 0, 0));
    },
    onFloorChanged() {
      if (this.form.floor) {
        if (!this.form.maxFloor) this.form.maxFloor = this.form.floor;
        else if (+this.form.floor > +this.form.maxFloor) this.form.maxFloor = this.form.floor;
      }
    },
    onMaxFloorChanged() {
      if (this.form.maxFloor) {
        if (!this.form.floor) this.form.floor = this.form.maxFloor;
        else if (+this.form.floor > +this.form.maxFloor) this.form.floor = this.form.maxFloor;
      }
    },
    isEstateTypeDisabled(item: any) {
      if (item) {
        if (this.isRoommate && item.key > 300 && item.key < 400) {
          return true;
        }
      }
      return false;
    },
    calculateArea(area: number | string, unit: string): number {
      if (!area) return 0;
      const areaNumber = +area.toString().replaceAll(',', '.');
      if (isNaN(areaNumber)) {
        return 0;
      }
      if (unit === 'a') {
        return areaNumber * 100;
      } else if (unit === 'ha') {
        return areaNumber * 10000;
      } else {
        return areaNumber;
      }
    },
  },
});
